import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { PageHeader, DarkPanel, dark } from '../components/ui';

const PIE_COLORS = [
  '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
  '#06b6d4', '#84cc16', '#ec4899', '#6366f1', '#14b8a6', '#f97316',
];

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function phaseStyle(phase) {
  switch (phase) {
    case 'Leading':
      return 'bg-emerald-400/15 text-emerald-300';
    case 'Improving':
      return 'bg-lime-400/15 text-lime-300';
    case 'Neutral':
      return 'bg-white/10 text-slate-300';
    case 'Weakening':
      return 'bg-amber-400/15 text-amber-300';
    case 'Lagging':
      return 'bg-rose-400/15 text-rose-300';
    default:
      return 'bg-white/10 text-slate-400';
  }
}

function pctClass(v) {
  if (v == null) return 'text-slate-500';
  const n = Number(v);
  if (n > 0) return 'text-emerald-400';
  if (n < 0) return 'text-rose-400';
  return 'text-slate-500';
}

/** Build SVG pie paths from sector RS scores (shifted so all slices positive). */
function SectorPie({ sectors }) {
  const slices = useMemo(() => {
    if (!sectors?.length) return [];
    const scores = sectors.map((s) => Number(s.rsScore) || 0);
    const min = Math.min(...scores);
    // Shift so weakest is still a thin slice; weight ≈ relative leadership
    const weights = scores.map((sc) => Math.max(sc - min + 1.5, 0.4));
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    let angle = -Math.PI / 2;
    const cx = 120;
    const cy = 120;
    const r = 100;
    return sectors.map((s, i) => {
      const frac = weights[i] / total;
      const sweep = frac * Math.PI * 2;
      const x1 = cx + r * Math.cos(angle);
      const y1 = cy + r * Math.sin(angle);
      angle += sweep;
      const x2 = cx + r * Math.cos(angle);
      const y2 = cy + r * Math.sin(angle);
      const large = sweep > Math.PI ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      const mid = angle - sweep / 2;
      const lx = cx + (r * 0.62) * Math.cos(mid);
      const ly = cy + (r * 0.62) * Math.sin(mid);
      return {
        path,
        color: PIE_COLORS[i % PIE_COLORS.length],
        etf: s.etf,
        sector: s.sector,
        pct: frac * 100,
        rs: s.rsScore,
        labelX: lx,
        labelY: ly,
        showLabel: frac >= 0.07,
      };
    });
  }, [sectors]);

  if (!slices.length) return null;

  return (
    <DarkPanel title="Relative strength mix" subtitle="Bigger slice = stronger vs SPY" bodyClassName="p-5">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <svg viewBox="0 0 240 240" className="w-52 h-52 sm:w-56 sm:h-56 shrink-0">
          {slices.map((sl) => (
            <path
              key={sl.etf}
              d={sl.path}
              fill={sl.color}
              stroke="#0f172a"
              strokeWidth="2"
              className="transition opacity-95 hover:opacity-100"
            >
              <title>
                {sl.sector} ({sl.etf}) · RS {fmtPct(sl.rs)} · {sl.pct.toFixed(1)}% of chart
              </title>
            </path>
          ))}
          {slices
            .filter((sl) => sl.showLabel)
            .map((sl) => (
              <text
                key={`${sl.etf}-lbl`}
                x={sl.labelX}
                y={sl.labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-white text-[10px] font-bold"
                style={{ fontSize: 10, fontWeight: 700 }}
              >
                {sl.etf}
              </text>
            ))}
        </svg>
        <div className="flex-1 w-full grid grid-cols-1 xs:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
          {slices.map((sl) => (
            <div key={sl.etf} className="flex items-center gap-2 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: sl.color }}
              />
              <span className="font-semibold text-white w-10">{sl.etf}</span>
              <span className="text-slate-400 truncate flex-1">{sl.sector}</span>
              <span className={`tabular-nums font-medium ${pctClass(sl.rs)}`}>{fmtPct(sl.rs)}</span>
            </div>
          ))}
        </div>
      </div>
    </DarkPanel>
  );
}

export default function SectorRotation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    api
      .get('/stock/sector-rotation')
      .then((r) => {
        setData(r.data);
        if (!r.data?.success && r.data?.message) setError(r.data.message);
      })
      .catch(() => setError('Could not load sector rotation'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const sectors = data?.sectors || [];

  return (
    <div className="pb-8">
      <PageHeader title="Sector rotation" />

      {loading && !data && (
        <p className="text-center text-slate-400 py-16 text-sm">Analyzing sectors…</p>
      )}

      {error && !sectors.length && (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 text-rose-800 px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {data?.summary && (
        <DarkPanel title="Rotation read" className="mb-4">
          <p className="text-sm leading-relaxed text-slate-200">{data.summary}</p>
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            {data.leaders?.length > 0 && (
              <div>
                <span className="text-slate-400">Leaders · </span>
                <span className="font-medium text-emerald-300">{data.leaders.join(', ')}</span>
              </div>
            )}
            {data.laggards?.length > 0 && (
              <div>
                <span className="text-slate-400">Laggards · </span>
                <span className="font-medium text-rose-300">{data.laggards.join(', ')}</span>
              </div>
            )}
          </div>
        </DarkPanel>
      )}

      {sectors.length > 0 && (
        <div className="mb-4">
          <SectorPie sectors={sectors} />
        </div>
      )}

      {sectors.length > 0 && (
        <DarkPanel title="Sectors" subtitle="Ranked by relative strength vs SPY" bodyClassName="p-0">
          <div className={dark.wrap}>
            <table className={dark.table}>
              <thead className={dark.thead}>
                <tr>
                  <th className={`${dark.th} text-left`}>#</th>
                  <th className={`${dark.th} text-left`}>Sector</th>
                  <th className={`${dark.th} text-left`}>ETF</th>
                  <th className={`${dark.th} text-right`}>Price</th>
                  <th className={`${dark.th} text-right`}>1D</th>
                  <th className={`${dark.th} text-right`}>1W</th>
                  <th className={`${dark.th} text-right`}>1M</th>
                  <th className={`${dark.th} text-right`}>3M</th>
                  <th className={`${dark.th} text-right`} title="Excess return vs SPY (weighted)">
                    RS vs SPY
                  </th>
                  <th className={`${dark.th} text-left`}>Phase</th>
                </tr>
              </thead>
              <tbody>
                {sectors.map((s) => (
                  <tr key={s.etf} className={dark.tr}>
                    <td className={`${dark.td} text-slate-500 tabular-nums`}>{s.rank}</td>
                    <td className={`${dark.td} font-medium text-white`}>{s.sector}</td>
                    <td className={dark.td}>
                      <Link
                        to={`/stock/${encodeURIComponent(s.etf)}`}
                        className="font-semibold text-white hover:text-emerald-300"
                      >
                        {s.etf}
                      </Link>
                    </td>
                    <td className={`${dark.td} text-right tabular-nums ${pctClass(s.ret1D)}`}>
                      {s.price != null ? `$${Number(s.price).toFixed(2)}` : '—'}
                    </td>
                    <td className={`${dark.td} text-right tabular-nums font-medium ${pctClass(s.ret1D)}`}>
                      {fmtPct(s.ret1D)}
                    </td>
                    <td className={`${dark.td} text-right tabular-nums font-medium ${pctClass(s.ret1W)}`}>
                      {fmtPct(s.ret1W)}
                    </td>
                    <td className={`${dark.td} text-right tabular-nums font-medium ${pctClass(s.ret1M)}`}>
                      {fmtPct(s.ret1M)}
                    </td>
                    <td className={`${dark.td} text-right tabular-nums font-medium ${pctClass(s.ret3M)}`}>
                      {fmtPct(s.ret3M)}
                    </td>
                    <td className={`${dark.td} text-right tabular-nums font-semibold ${pctClass(s.rsScore)}`}>
                      {fmtPct(s.rsScore)}
                    </td>
                    <td className={dark.td}>
                      <span
                        className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${phaseStyle(
                          s.phase
                        )}`}
                      >
                        {s.phase}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DarkPanel>
      )}

      {sectors.some((s) => s.note) && (
        <DarkPanel title="Notes" className="mt-4" bodyClassName="space-y-2 p-4">
          {sectors
            .filter((s) => s.note)
            .map((s) => (
              <p key={s.etf} className="text-xs text-slate-300">
                <span className="font-semibold text-white">{s.sector}</span> — {s.note}
              </p>
            ))}
        </DarkPanel>
      )}

      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
        Methodology: RS score blends excess return vs SPY (0.35×1W + 0.40×1M + 0.25×3M). Pie segments
        scale with relative strength (stronger sectors = larger slices). Phases are heuristic labels,
        not investment advice.
      </p>
    </div>
  );
}
